import { useState } from "react"
import { IconArrowRight } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { extractApiError } from "@/lib/api-errors"
import { AppointmentTypeChangeOutcome, useChangeAppointmentTypeMutation } from "../../api/rescheduleApi"
import { SITE_TIME_ZONE, longDateLabel, minutesToLabel, zonedDateAndMinutes } from "../../lib/siteTime"
import { SLOT_TYPES, SLOT_TYPE_LABEL, eventTypeSlugFor, slotTypeLabelForSlug } from "../../lib/slotTypes"
import type { BookableEventTypeSlug } from "../../lib/slotTypes"
import { AppointmentDialogFooter } from "../AppointmentDialogFooter"
import { describeRescheduleFailure, isRescheduleReasonValid } from "./RescheduleAction"
import type { BookingAction, BookingActionFormProps } from "./types"
import { MAX_NOTE_LENGTH, isChangeable } from "./types"
import { useReportBusy } from "./useReportBusy"

/** What the confirm step promises: a change of type moves neither the host nor the time. */
export const SAME_HOST_SAME_TIME = "Same host, same time"

/** One type the appointment could change to. */
export interface ChangeTypeChoice {
  /** The event type slug sent to the server. */
  value: BookableEventTypeSlug
  /** "Public", "Private" or "Managed" — the grid's names (see `lib/slotTypes`). */
  label: string
}

/**
 * The types offered, in the grid's order, leaving out the appointment's own. With its own type
 * unknown (a server that predates `eventTypeSlug`) all three are offered and the server refuses the
 * one it already is.
 */
export function changeTypeChoices(currentSlug: string | null | undefined): ChangeTypeChoice[] {
  return SLOT_TYPES
    .map(type => ({ value: eventTypeSlugFor(type), label: SLOT_TYPE_LABEL[type] }))
    .filter(choice => choice.value !== currentSlug)
}

/**
 * A refused or failed change in words. The server's message comes first: a 409 names the check that
 * refused it (not scheduled for that type, booked, away, the daily limit, the notice), and a 502 says
 * whether the household still has the old appointment. Then the status.
 */
export function describeChangeTypeFailure(error: unknown): string {
  const failure = error as { status?: number | string; data?: unknown }
  const detail = extractApiError(failure?.data)
  if (detail) return detail
  if (failure?.status === 403) return "Only a manager can change an appointment's type."
  if (failure?.status === 409) return "The type cannot be changed at this time. The appointment has not been changed."
  return describeRescheduleFailure(error)
}

/**
 * A manager changing a booked appointment's type, keeping its host and time (AB#767). Recorded as a
 * reschedule — the booking reference changes — and the household is told nothing, because nothing
 * they were told has changed. Every check is the server's; a refusal is shown here, in place.
 */
export const ChangeTypeAction: BookingAction = {
  id: "changingType",
  label: "Change type",
  triggerVariant: "outline",
  isAvailable: ctx => isChangeable(ctx) && ctx.canManage,
  Form: ChangeTypeForm,
}

function ChangeTypeForm({ ctx, onClose, onDone, onBusyChange }: BookingActionFormProps) {
  const [change, changeState] = useChangeAppointmentTypeMutation()

  const [step, setStep] = useState<ChangeTypeStep>("choose")
  const [chosenSlug, setChosenSlug] = useState("")
  const [reason, setReason] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  const busy = changeState.isLoading
  useReportBusy(onBusyChange, busy)

  const currentSlug = ctx.statusView?.eventTypeSlug
  const start = zonedDateAndMinutes(ctx.booking.start, SITE_TIME_ZONE)

  async function submit() {
    if (!chosenSlug || !isRescheduleReasonValid(reason) || busy) return
    setError(null)

    const result = await change({
      uid: ctx.uid,
      requestId: ctx.requestId,
      newEventTypeSlug: chosenSlug,
      reason: reason.trim(),
    })

    if ("error" in result) {
      // Back to the choice, with the server's reason beside it: nothing was changed.
      setError(describeChangeTypeFailure(result.error))
      setStep("choose")
      return
    }

    // Changed: the uid this dialog was opened on is cancelled. Stay only for something staff must read.
    if (result.data.outcome === AppointmentTypeChangeOutcome.Changed && result.data.message) {
      setWarning(result.data.message)
      return
    }

    onClose()
  }

  return (
    <ChangeTypeView
      step={step}
      currentLabel={slotTypeLabelForSlug(currentSlug)}
      choices={changeTypeChoices(currentSlug)}
      chosenSlug={chosenSlug}
      reason={reason}
      error={error}
      warning={warning}
      busy={busy}
      hostName={ctx.booking.hosts[0]?.name ?? ctx.statusView?.hostName ?? undefined}
      when={`${longDateLabel(start.date)} · ${minutesToLabel(start.minutes)}`}
      attendeeName={ctx.attendeeName}
      onChoose={value => { setChosenSlug(value); setError(null) }}
      onReason={setReason}
      onNext={() => { setError(null); setStep("confirm") }}
      onBack={() => setStep("choose")}
      onSubmit={submit}
      onCancel={onDone}
      onClose={onClose}
    />
  )
}

export type ChangeTypeStep = "choose" | "confirm"

export interface ChangeTypeViewProps {
  step: ChangeTypeStep
  /** The appointment's own type, named as the grid names it; null when unknown. */
  currentLabel: string | null
  choices: ChangeTypeChoice[]
  chosenSlug: string
  reason: string
  /** The server's refusal, shown in place. */
  error: string | null
  /** Something staff must read about a change that happened. */
  warning: string | null
  busy: boolean
  hostName?: string
  /** "Thursday, Oct 8 · 10:00 AM", Pacific. */
  when: string
  attendeeName?: string
  onChoose: (slug: string) => void
  onReason: (reason: string) => void
  onNext: () => void
  onBack: () => void
  onSubmit: () => void
  onCancel: () => void
  onClose: () => void
}

/** The form's two steps — choose a type and give a reason, then confirm — with no state of its own. */
export function ChangeTypeView(props: ChangeTypeViewProps) {
  const { step, currentLabel, choices, chosenSlug, reason, error, warning, busy } = props
  const chosen = choices.find(choice => choice.value === chosenSlug)
  const canContinue = !!chosen && isRescheduleReasonValid(reason) && !busy && !warning

  return (
    <>
      <section>
        {step === "choose" ? (
          <>
            <Label htmlFor="change-type-select" className="text-xs font-medium">
              New type
              {currentLabel && <span className="text-muted-foreground"> (now {currentLabel})</span>}
            </Label>
            <Select value={chosenSlug} onValueChange={props.onChoose} disabled={busy || !!warning}>
              <SelectTrigger id="change-type-select" className="mt-1.5 w-full">
                <SelectValue placeholder="Choose a type" />
              </SelectTrigger>
              <SelectContent>
                {choices.map(choice => (
                  <SelectItem key={choice.value} value={choice.value}>{choice.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Label htmlFor="change-type-reason" className="mt-3 block text-xs font-medium">
              Reason <span className="text-muted-foreground">(required; internal, never sent to the household)</span>
            </Label>
            <Textarea
              id="change-type-reason"
              className="mt-1.5"
              rows={2}
              maxLength={MAX_NOTE_LENGTH}
              value={reason}
              disabled={busy || !!warning}
              placeholder="e.g. Should have been booked as private"
              onChange={event => props.onReason(event.target.value)}
            />
          </>
        ) : (
          <div data-testid="change-type-confirm" className="space-y-2 text-sm">
            <p className="flex items-center gap-2 font-medium">
              <span>{currentLabel ?? "Current type"}</span>
              <IconArrowRight className="size-4" aria-hidden />
              <span>{chosen?.label}</span>
            </p>
            <p>
              <strong>{SAME_HOST_SAME_TIME}</strong>: {props.when}{props.hostName ? ` with ${props.hostName}` : ""}.
            </p>
            <p className="text-xs text-muted-foreground">
              {props.attendeeName ?? "The attendee"} is not told: nothing they were told changes. The booking
              reference changes; their confirmation number still works.
            </p>
          </div>
        )}
      </section>

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {warning && <p role="alert" className="text-sm text-amber-700">{warning}</p>}

      <AppointmentDialogFooter>
        {warning ? (
          <Button onClick={props.onClose}>Close</Button>
        ) : step === "choose" ? (
          <>
            <Button variant="outline" disabled={busy} onClick={props.onCancel}>Cancel</Button>
            <Button disabled={!canContinue} onClick={props.onNext}>Continue</Button>
          </>
        ) : (
          <>
            <Button variant="outline" disabled={busy} onClick={props.onBack}>Back</Button>
            <Button disabled={!canContinue} onClick={props.onSubmit}>
              {busy ? "Changing…" : "Change type"}
            </Button>
          </>
        )}
      </AppointmentDialogFooter>
    </>
  )
}
